import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  ResponsiveContainer, PieChart, Pie, Legend,
  AreaChart, Area, CartesianGrid, ReferenceLine,
  LabelList,
} from 'recharts';

// ─── Shared constants ────────────────────────────────────────────────────────
const PALETTE = ['#008385','#1a7f4b','#6b45c8','#b45309','#c0392b','#00b3b5','#374151','#9ca3af'];
const GENDER_COLORS = { no_preference: '#008385', male: '#1a7f4b', female: '#6b45c8' };
const DURATION_COLORS = { 1: '#008385', 2: '#1a7f4b', 3: '#6b45c8', 4: '#b45309' };
const CAT_COLORS = { 1: '#008385', 2: '#b45309', 3: '#6b45c8', 4: '#c0392b' };

const crColor  = (cr) => cr >= 30 ? '#1a7f4b' : cr >= 15 ? '#b45309' : '#c0392b';
const crBg     = (cr) => cr >= 30 ? 'rgba(26,127,75,0.08)' : cr >= 15 ? 'rgba(180,83,9,0.08)' : 'rgba(192,57,43,0.08)';

const formatNumber = (num) => (num == null ? '—' : new Intl.NumberFormat('en-GB').format(num));

const SectionLabel = ({ children, color: labelColor = '#008385' }) => (
  <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
    <div style={{ width:4, height:22, background:labelColor, borderRadius:2, flexShrink:0 }} />
    <div style={{ fontSize:15, fontWeight:700, color:'#111827' }}>{children}</div>
    <div style={{ flex:1, height:1, background:'#f0f0f0' }} />
  </div>
);

const CRBadge = ({ cr }) => (
  <span style={{ display:'inline-block', padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:crBg(cr), color:crColor(cr) }}>
    {(+cr).toFixed(1)}% CR
  </span>
);

const ProgressBar = ({ pct, color, height = 5 }) => (
  <div style={{ width:'100%', background:'#f3f4f6', borderRadius:99, height, overflow:'hidden', marginTop:4 }}>
    <div style={{ width:`${Math.min(pct,100)}%`, height:'100%', background:color, borderRadius:99, transition:'width 0.4s ease' }} />
  </div>
);

// ─── Appointment Type Breakdown ──────────────────────────────────────────────
const AppointmentTypeBreakdown = ({ by_appointment_type }) => {
  if (!by_appointment_type?.length) return null;
  const TYPE_COLORS = ['#008385','#6b45c8','#b45309','#c0392b','#1a7f4b'];
  return (
    <div>
      <SectionLabel>By Appointment Type</SectionLabel>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {by_appointment_type.map((row, i) => {
          const color = TYPE_COLORS[i % TYPE_COLORS.length];
          return (
            <div key={i} style={{ padding:'10px 14px', border:'1px solid #f3f4f6', borderRadius:10 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:10, height:10, borderRadius:2, background:color }} />
                  <span style={{ fontWeight:600, fontSize:13 }}>{row.name}</span>
                </div>
                <div style={{ display:'flex', gap:16, fontSize:12, alignItems:'center' }}>
                  <span style={{ color:'#6b7280' }}>Sessions: <strong style={{ color:'#111827' }}>{formatNumber(row.total)}</strong></span>
                  <span style={{ color:'#6b7280' }}>Booked: <strong style={{ color:'#1a7f4b' }}>{formatNumber(row.confirmed)}</strong></span>
                  <CRBadge cr={row.cr} />
                </div>
              </div>
              <ProgressBar pct={row.pct} color={color} />
              <div style={{ fontSize:10, color:'#9ca3af', marginTop:3 }}>{row.pct}% of sessions</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Product Category Breakdown ───────────────────────────────────────────────
const ProductCategoryBreakdown = ({ by_product_category }) => {
  if (!by_product_category?.length) return null;
  return (
    <div>
      <SectionLabel>By Product Category</SectionLabel>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
        {by_product_category.map((row, i) => {
          const color = CAT_COLORS[row.product_category_id] || PALETTE[i];
          return (
            <div key={i} style={{ padding:'14px 16px', border:`1px solid ${color}25`, borderRadius:10, background:`${color}06` }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                <div style={{ width:10, height:10, borderRadius:2, background:color }} />
                <span style={{ fontWeight:600, fontSize:13, color:'#111827' }}>{row.name}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:22, fontWeight:800, color }}>{formatNumber(row.confirmed)}</div>
                  <div style={{ fontSize:10, color:'#9ca3af', textTransform:'uppercase' }}>Confirmed</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:22, fontWeight:800, color:'#374151' }}>{formatNumber(row.total)}</div>
                  <div style={{ fontSize:10, color:'#9ca3af', textTransform:'uppercase' }}>Sessions</div>
                </div>
              </div>
              <ProgressBar pct={row.pct} color={color} height={6} />
              <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
                <span style={{ fontSize:10, color:'#9ca3af' }}>{row.pct}% of total</span>
                <CRBadge cr={row.cr} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Duration Breakdown ───────────────────────────────────────────────────────
const DurationBreakdown = ({ by_duration }) => {
  if (!by_duration?.length) return null;
  return (
    <div>
      <SectionLabel>By Appointment Duration</SectionLabel>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
        {by_duration.map((row, i) => {
          const color = DURATION_COLORS[row.duration_id] || PALETTE[i];
          return (
            <div key={i} style={{ flex:1, minWidth:120, padding:'14px 16px', border:`1px solid ${color}25`, borderRadius:10, background:`${color}06` }}>
              <div style={{ fontSize:16, fontWeight:800, color, marginBottom:2 }}>{row.label}</div>
              <div style={{ fontSize:11, color:'#9ca3af', marginBottom:10 }}>{row.pct}% of sessions</div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:6 }}>
                <span style={{ color:'#6b7280' }}>Sessions <strong style={{ color:'#111827' }}>{formatNumber(row.total)}</strong></span>
              </div>
              <ProgressBar pct={row.pct} color={color} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Gender Breakdown ─────────────────────────────────────────────────────────
const GenderBreakdown = ({ by_gender }) => {
  if (!by_gender?.length) return null;
  const ICONS = { no_preference: '\u{1F464}', male: '\u2642\uFE0F', female: '\u2640\uFE0F' };
  const ICON_STYLE = { fontSize: 28, lineHeight: 1, display: 'block', marginBottom: 4 };
  return (
    <div>
      <SectionLabel>By Preferred Gender</SectionLabel>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
        {by_gender.map((row, i) => {
          const color = GENDER_COLORS[row.gender] || PALETTE[i];
          return (
            <div key={i} style={{ flex:1, minWidth:140, padding:'14px 16px', border:`1px solid ${color}25`, borderRadius:10, background:`${color}06`, textAlign:'center' }}>
              <div style={ICON_STYLE} aria-hidden="true">{ICONS[row.gender] || '\u{1F464}'}</div>
              <div style={{ fontWeight:600, fontSize:13, color:'#111827', marginBottom:8 }}>{row.label}</div>
              <div style={{ fontSize:22, fontWeight:800, color }}>{formatNumber(row.total)}</div>
              <div style={{ fontSize:10, color:'#9ca3af', marginBottom:10 }}>sessions</div>
              <ProgressBar pct={row.pct} color={color} height={4} />
              <div style={{ fontSize:10, color:'#9ca3af', marginTop:3 }}>{row.pct}% share</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Language Breakdown ───────────────────────────────────────────────────────
const LanguageBreakdown = ({ by_language }) => {
  if (!by_language?.length) return null;
  return (
    <div>
      <SectionLabel>By Preferred Language</SectionLabel>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {by_language.map((row, i) => {
          const color = PALETTE[i % PALETTE.length];
          return (
            <div key={i} style={{ padding:'10px 14px', border:'1px solid #f3f4f6', borderRadius:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:10, height:10, borderRadius:2, background:color }} />
                  <span style={{ fontWeight:600, fontSize:13 }}>{row.name}</span>
                </div>
                <div style={{ display:'flex', gap:16, fontSize:12, alignItems:'center' }}>
                  <span style={{ color:'#6b7280' }}>{formatNumber(row.total)} sessions</span>
                  <span style={{ color:'#1a7f4b', fontWeight:700 }}>✓ {formatNumber(row.confirmed)}</span>
                  <CRBadge cr={row.cr} />
                  <span style={{ color:'#9ca3af', fontSize:11 }}>{row.pct}%</span>
                </div>
              </div>
              <ProgressBar pct={row.pct} color={color} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Clinic Performance Table ─────────────────────────────────────────────────
const ClinicPerformanceTable = ({ by_clinic }) => {
  if (!by_clinic?.length) return null;
  const sorted = [...by_clinic].sort((a, b) => b.cr - a.cr);
  const max = sorted[0]?.cr || 100;
  return (
    <div>
      <SectionLabel>Clinic Performance</SectionLabel>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', minWidth: 480, borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ background:'#f9fafb' }}>
              {['Clinic', 'Sessions', 'Confirmed', 'Performance'].map((h, i) => (
                <th key={i} style={{ padding:'7px 10px', fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em', textAlign: i === 0 ? 'left' : 'right', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                <td style={{ padding:'8px 10px', fontWeight:600, color:'#111827' }}>{row.clinic_name}</td>
                <td style={{ padding:'8px 10px', textAlign:'right', color:'#374151' }}>{formatNumber(row.total)}</td>
                <td style={{ padding:'8px 10px', textAlign:'right', color:'#1a7f4b', fontWeight:700 }}>{formatNumber(row.confirmed)}</td>


                <td style={{ padding:'8px 10px', minWidth:100 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ flex:1, background:'#f3f4f6', borderRadius:99, height:6, overflow:'hidden' }}>
                      <div style={{ width:`${(row.cr / max) * 100}%`, height:'100%', background:crColor(row.cr), borderRadius:99 }} />
                    </div>
                    <span style={{ fontSize:10, color:'#9ca3af', flexShrink:0 }}>{row.cr}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Time of Day: bucket time-range definitions ──────────────────────────────
const TIME_BUCKET_DEFS = {
  'Night':          '00:00–04:59',
  'Early Morning':  '05:00–08:59',
  'Morning':        '09:00–12:59',
  'Afternoon':      '13:00–16:59',
  'Late Afternoon': '17:00–19:59',
  'Evening':        '20:00–23:59',
};

// ─── Time of Day Bar Chart ────────────────────────────────────────────────────
const TimeOfDayChart = ({ by_hour_bucket }) => {
  const [hovIdx, setHovIdx] = useState(null);
  if (!by_hour_bucket?.length) return null;
  const data = by_hour_bucket.map(b => ({ ...b, label: b.bucket.split('(')[0].trim() }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 14px', fontSize:12, boxShadow:'0 4px 16px rgba(0,0,0,0.10)' }}>
        <div style={{ fontWeight:700, color:'#374151', marginBottom:6 }}>🕐 {label}</div>
        <div style={{ color:'#6b7280' }}>Total sessions: <strong style={{ color:'#111827' }}>{formatNumber(d?.total)}</strong></div>
        <div style={{ color:'#1a7f4b' }}>Confirmed: <strong>{formatNumber(d?.confirmed)}</strong></div>
        <div style={{ marginTop:4, color:crColor(d?.cr), fontWeight:700 }}>{d?.cr}% conversion</div>
      </div>
    );
  };

  // Always-visible label INSIDE the total bar (count, top-aligned)
  const TotalLabel = ({ x, y, width, height, index }) => {
    const row = data[index];
    if (!row || !row.total || height < 18) return null;
    return (
      <text x={x + width / 2} y={y + 13} textAnchor="middle" fontSize={9} fill="#374151" fontWeight={700}>
        {formatNumber(row.total)}
      </text>
    );
  };

  // Always-visible label: count inside confirmed bar + CR% badge above it
  const ConfirmedLabel = ({ x, y, width, height, index }) => {
    const row = data[index];
    if (!row) return null;
    const badgeW = 36;
    return (
      <g>
        {/* CR% pill above the bar */}
        <rect x={x + width / 2 - badgeW / 2} y={y - 18} width={badgeW} height={14} rx={4} fill={crColor(row.cr)} opacity={0.22} />
        <text x={x + width / 2} y={y - 7} textAnchor="middle" fontSize={9} fontWeight={800} fill={crColor(row.cr)}>
          {row.cr}%
        </text>
        {/* Count inside bar */}
        {height >= 18 && (
          <text x={x + width / 2} y={y + 13} textAnchor="middle" fontSize={9} fill="#fff" fontWeight={700}>
            {formatNumber(row.confirmed)}
          </text>
        )}
      </g>
    );
  };

  return (
    <div>
      <SectionLabel>Sessions by Time of Day</SectionLabel>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top:26, right:8, left:-8, bottom:0 }} barCategoryGap="30%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize:11, fill:'#6b7280' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize:10, fill:'#9ca3af' }} tickLine={false} axisLine={false} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(0,131,133,0.06)', radius:6 }} />
          <Bar dataKey="total" name="Total" radius={[6,6,0,0]} onMouseEnter={(_, i) => setHovIdx(i)} onMouseLeave={() => setHovIdx(null)}>
            {data.map((d, i) => <Cell key={i} fill={i === hovIdx ? '#00a8aa' : '#a8e6e7'} />)}
            <LabelList content={TotalLabel} />
          </Bar>
          <Bar dataKey="confirmed" name="Confirmed" radius={[6,6,0,0]}>
            {data.map((d, i) => <Cell key={i} fill={crColor(d.cr)} opacity={i === hovIdx ? 1 : 0.85} />)}
            <LabelList content={ConfirmedLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div style={{ display:'flex', gap:12, marginTop:12, justifyContent:'center' }}>
        <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#6b7280' }}>
          <span style={{ width:10, height:10, borderRadius:2, background:'#d1fafa', border:'1px solid #9ca3af', display:'inline-block' }} />Total Sessions
        </span>
        <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#6b7280' }}>
          <span style={{ width:10, height:10, borderRadius:2, background:'#008385', display:'inline-block' }} />Confirmed (CR% shown above bar)
        </span>
      </div>
      {/* Time bucket definitions legend */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 16px', marginTop:14, padding:'8px 12px', background:'#f9fafb', borderRadius:8 }}>
        {data.map((d, i) => (
          <span key={i} style={{ fontSize:10, color:'#6b7280', whiteSpace:'nowrap' }}>
            <strong style={{ color:'#374151' }}>{d.label}</strong>&nbsp;{TIME_BUCKET_DEFS[d.label] || ''}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── Day of Week Chart ────────────────────────────────────────────────────────
const DayOfWeekChart = ({ by_day_of_week }) => {
  const [hovIdx, setHovIdx] = useState(null);
  if (!by_day_of_week?.length) return null;
  const DAY_SHORT = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat', Sunday:'Sun' };
  const data = by_day_of_week.map(r => ({ ...r, label: DAY_SHORT[r.day] || r.day, isWeekend: r.day === 'Saturday' || r.day === 'Sunday' }));
  const avgCr = +(data.reduce((s, d) => s + +d.cr, 0) / data.length).toFixed(1);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 14px', fontSize:12, boxShadow:'0 4px 16px rgba(0,0,0,0.10)' }}>
        <div style={{ fontWeight:700, color:'#374151', marginBottom:6 }}>📅 {d?.day}</div>
        <div style={{ color:'#6b7280' }}>Sessions: <strong style={{ color:'#111827' }}>{formatNumber(d?.total)}</strong></div>
        <div style={{ color:'#1a7f4b' }}>Confirmed: <strong>{formatNumber(d?.confirmed)}</strong></div>
        <div style={{ marginTop:4, color:crColor(d?.cr), fontWeight:700 }}>{d?.cr}% CR {d?.isWeekend ? '(Weekend)' : ''}</div>
      </div>
    );
  };

  // Always-visible label: CR% on top line, session count below it
  const DayLabel = ({ x, y, width, index }) => {
    const row = data[index];
    if (!row) return null;
    const isHov = index === hovIdx;
    return (
      <g>
        <text x={x + width / 2} y={y - 16} textAnchor="middle" fontSize={10} fontWeight={800} fill={crColor(row.cr)}>
          {row.cr}%
        </text>
        <text x={x + width / 2} y={y - 4} textAnchor="middle" fontSize={9} fill="#4b5563" fontWeight={600}>
          {formatNumber(row.total)}
        </text>
      </g>
    );
  };

  return (
    <div>
      <SectionLabel>Sessions by Day of Week</SectionLabel>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top:38, right:8, left:-8, bottom:0 }} barCategoryGap="25%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize:11, fill:'#6b7280' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize:10, fill:'#9ca3af' }} tickLine={false} axisLine={false} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill:'rgba(0,131,133,0.06)', radius:6 }} />
          <ReferenceLine y={avgCr} stroke="#6b7280" strokeDasharray="4 4" label={{ value:`Avg ${avgCr}%`, position:'insideTopRight', fontSize:10, fill:'#6b7280' }} />
          <Bar dataKey="cr" name="CR%" radius={[6,6,0,0]}
            onMouseEnter={(_, i) => setHovIdx(i)}
            onMouseLeave={() => setHovIdx(null)}
          >
            {data.map((d, i) => (
              <Cell key={i}
                fill={d.isWeekend ? '#b45309' : crColor(d.cr)}
                opacity={hovIdx === null || hovIdx === i ? 1 : 0.7}
              />
            ))}
            <LabelList content={DayLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div style={{ display:'flex', gap:12, marginTop:12, justifyContent:'center' }}>
        <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#6b7280' }}>
          <span style={{ width:10, height:10, borderRadius:2, background:'#1a7f4b', display:'inline-block' }} />High CR (≥30%)
        </span>
        <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#6b7280' }}>
          <span style={{ width:10, height:10, borderRadius:2, background:'#b45309', display:'inline-block' }} />Weekends
        </span>
        <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'#6b7280' }}>
          <span style={{ width:10, height:10, borderRadius:2, background:'#c0392b', display:'inline-block' }} />Low CR
        </span>
      </div>
    </div>
  );
};

// ─── Route Breakdown ─────────────────────────────────────────────────────────
const ROUTE_COLORS = { 'By Clinic': '#008385', 'By Time': '#6b45c8' };

const RouteCard = ({ row, color, fullWidth = false }) => (
  <div style={{
    flex: fullWidth ? '1 1 100%' : '1 1 calc(50% - 5px)',
    minWidth: fullWidth ? '100%' : 160,
    padding:'14px 16px',
    border:`1px solid ${color}20`,
    borderRadius:10,
    background:'#fff',
    boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
  }}>
    <div style={{ display:'flex', alignItems:'center', marginBottom:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
        <div style={{ width:8, height:8, borderRadius:2, background:color, flexShrink:0 }} />
        <span style={{ fontWeight:700, fontSize:13, color:'#111827' }}>{row.name}</span>
      </div>
    </div>
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:18, fontWeight:800, color:'#111827', lineHeight:1.1 }}>{formatNumber(row.total)}</div>
      <div style={{ fontSize:10, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.05em', marginTop:3 }}>Count</div>
    </div>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
      <span style={{ fontSize:10, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.04em' }}>Share of total</span>
      <span style={{ fontSize:11, fontWeight:700, color }}>{row.pct}%</span>
    </div>
    <ProgressBar pct={row.pct} color={color} height={5} />
  </div>
);

const RouteBreakdown = ({ by_route }) => {
  if (!by_route?.length) return null;
  // Separate "Abandoned on 2nd Step" (or any abandoned route) from clinic/time routes
  const abandoned = by_route.find(r => r.name?.toLowerCase().includes('abandon'));
  const routes    = by_route.filter(r => !r.name?.toLowerCase().includes('abandon'));
  return (
    <div>
      <SectionLabel>Booking Route — Clinic vs Time</SectionLabel>
      {/* Abandoned: full width */}
      {abandoned && (
        <div style={{ marginBottom: 10 }}>
          <RouteCard row={abandoned} color="#b45309" fullWidth />
        </div>
      )}
      {/* By Clinic + By Time: side-by-side */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        {routes.map((row, i) => {
          const color = ROUTE_COLORS[row.name] || PALETTE[i];
          return <RouteCard key={i} row={row} color={color} />;
        })}
      </div>
    </div>
  );
};

// ─── Canonical step definitions (top → bottom of the booking funnel) ─────────
// Each canonical step has:
//   key   — internal reference
//   label — display label shown in the funnel chart
//   aliases — every raw event/label string the backend or patient app may emit
//             for this step (handles old human-readable AND new snake_case names)
const CANONICAL_STEPS = [
  // ══ Forward journey — primary funnel ═══════════════════════════════════════
  { key: 'flow_started',         label: 'Booking flow started, appointment type selected',
    aliases: ['booking_flow_started', 'booking flow started',
              'booking flow started, appointment type selected', 'appointment type selected'] },

  // B — combined route (aggregated in BookingFunnel from C1 + C2)
  { key: 'route_selected',       label: 'Selected Booking Route',
    aliases: [
      'selected booking route', 'booking_route_selected', 'booking route selected',
      'selected booking route',
    ] },

  // C1 — Clinic path
  { key: 'clinic_route_selected', label: 'Selected Book by Clinic',
    aliases: [
      'book at preferred clinic selected', 'book at Preferred Clinic selected',
      'preferred_clinic_selected', 'selected book by clinic', 'selected_book_by_clinic',
    ] },

  // D1 — Clinic path
  { key: 'clinic_nav',           label: 'Shown Clinic Selection',
    aliases: [
      'navigate to clinic selection', 'navigate_to_clinic_selection',
      'navigate to clinic selection page',
      'navigate_to_clinc_selection_page',
      'shown clinic selection',
    ] },

  // E1 — Clinic path
  { key: 'clinic_picked',        label: 'Clinic Selected',
    aliases: ['clinic_selected', 'picked clinic', 'clinic selected'] },

  // C2 — Time path
  { key: 'time_route_selected',  label: 'Selected Book by Time',
    aliases: [
      'book at preferred time selected', 'book at Preferred Time selected',
      'selected book by time', 'selected_book_by_time',
    ] },

  // D2 — Time path (time blocks shown)
  { key: 'pref_time_shown',      label: 'Shown Time Blocks',
    aliases: ['selected time window', 'preferred_time_fetched', 'preferred time fetched', 'shown time blocks'] },

  // E2 — Time path (user picks a time block)
  { key: 'pref_time_selected',   label: 'Selected Time Block',
    aliases: ['selected_preferred_time', 'selected preferred time', 'preference selected', 'selected time block'] },

  // G shared — available slots displayed
  { key: 'slots_browsed',        label: 'Shown Time Slots',
    aliases: ['browsed available slots', 'browsed_available_slots', 'shown time slots'] },

  // H shared — slot reserved
  { key: 'slot_blocked',         label: 'Time Slot Selected',
    aliases: ['slot_blocked', 'slot reserved', 'time slot selected'] },

  // I shared
  { key: 'patient_form',         label: 'Proceeded to Patient Form',
    aliases: ['navigate_to_appointment_summary_page', 'navigate to appointment summary page', 'proceeded to patient form'] },

  // J shared — email/phone merged
  { key: 'email_phone',          label: 'Email and / or Phone validated',
    aliases: ['email and / or phone validated', 'entered_email_address', 'entered email address', 'entered_phone_number', 'entered phone number'] },

  // K shared
  { key: 'payment_init',         label: 'Payment Initiated',
    aliases: ['payment_initiated', 'payment initiated'] },

  // L shared
  { key: 'booked',               label: 'Booking Completed',
    aliases: ['booking_completed', 'booking completed'] },

  // ══ API error states ════════════════════════════════════════════════════════
  { key: 'slots_fail',           label: 'Available-slots fetch failed',    aliases: ['available-slots fetch failed'] },
  { key: 'pref_clinics_fail',    label: 'Preferred-clinics fetch failed',  aliases: ['preferred-clinics fetch failed'] },
  { key: 'pref_time_fail',       label: 'Preferred-time fetch failed',     aliases: ['referred-time/page/1 fetch failed', 'preferred-time fetch failed'] },
  { key: 'address_fail',         label: 'Address lookup failed',           aliases: ['get-address fetch failed'] },

  // ══ Back-navigation ════════════════════════════════════════════════════════
  { key: 'clinic_unselected',    label: 'Clinic unselected',        aliases: ['clinic_unselected', 'clinic unselected'] },
  { key: 'time_unselected',      label: 'Unselected preferred time', aliases: ['unselected_preferred_time', 'unselected preferred time'] },

  // ══ Session / expiry events (shown below funnel) ════════════════════════════
  // M
  { key: 'session_warning',      label: 'Session Expiry Pop-up Shown',
    aliases: ['session warning shown', 'session_warning_shown', 'session expiry pop-up shown'] },
  // O
  { key: 'slot_expired',         label: 'Slot Reservation Expired',
    aliases: [
      'going back to appointment type selection', 'going_back_to_appointment_type_selection',
      'going back to appointment type type selection',
      'going_back_to_appointment_duration_selection', 'going back to appointment duration selection',
      'slot reservation expired',
    ] },

  { key: 'session_expired',  label: 'Session expired',             aliases: ['session expired', 'Session Expired'] },
  { key: 'session_extended', label: 'Session extended',            aliases: ['session extended', 'session_extended'] },
  { key: 'session_declined', label: 'Session extension declined',  aliases: ['session extension declined', 'session_extension_declined'] },
  { key: 'coupon_ok',        label: 'Coupon validation success',   aliases: ['coupon validation success', 'coupon_validation_success'] },
  { key: 'reschedule_done',  label: 'Booking reschedule completed',aliases: ['booking_reschedule_completed', 'booking reschedule completed'] },
  { key: 'cancellation',     label: 'Appointment cancellation initiated', aliases: ['appointment cancellation initiated'] },
  { key: 'delete_booking',   label: 'Delete booking',              aliases: ['delete booking'] },
];

const _LABEL_INDEX_MAP = (() => {
  const map = {};
  CANONICAL_STEPS.forEach((step, idx) => {
    step.aliases.forEach(alias => { map[alias.toLowerCase()] = idx; });
    map[step.label.toLowerCase()] = idx;
    map[step.key.toLowerCase()] = idx;
  });
  return map;
})();

const stepOrderIndex = (label) => {
  const l = (label || '').toLowerCase().trim();
  if (l in _LABEL_INDEX_MAP) return _LABEL_INDEX_MAP[l];
  for (let i = 0; i < CANONICAL_STEPS.length; i++) {
    const step = CANONICAL_STEPS[i];
    if (step.aliases.some(a => l.includes(a.toLowerCase())) || l.includes(step.label.toLowerCase())) return i;
  }
  return 999;
};

const canonicalLabel = (rawLabel) => {
  const l = (rawLabel || '').toLowerCase().trim();
  const idx = stepOrderIndex(l);
  return idx < CANONICAL_STEPS.length ? CANONICAL_STEPS[idx].label : rawLabel;
};

// ─── Booking Funnel (sequential journey visualisation) ───────────────────────
const BookingFunnel = ({ funnel, color }) => {
  if (!funnel?.length) return null;

  // Build a lookup: canonKey → count
  const countByKey = {};
  funnel.forEach(row => {
    const idx  = stepOrderIndex(row.label);
    const step = CANONICAL_STEPS[idx];
    console.log(`[Funnel] "${row.label}" → key:${step?.key ?? 'UNMAPPED'} count:${row.count}`);
    if (!step) return;
    countByKey[step.key] = (countByKey[step.key] || 0) + (row.count || 0);
  });


  const c = (key) => countByKey[key] || 0;

  // Step B = sum of C1 (clinic route) + C2 (time route)
  const routeCount = c('clinic_route_selected') + c('time_route_selected');

  // Max count across ALL displayed steps (main funnel + session events)
  const allCounts = [
    c('flow_started'), routeCount,
    c('clinic_route_selected'), c('clinic_nav'), c('clinic_picked'), c('pref_time_selected'), c('slots_browsed'), c('slot_blocked'),
    c('time_route_selected'), c('pref_time_shown'), c('pref_time_selected'), c('slots_browsed'), c('slot_blocked'),
    c('patient_form'), c('email_phone'), c('payment_init'), c('booked'),
    // session / expiry steps must be included so bars don't overflow
    c('session_warning'), c('slot_expired'), c('session_extended'), c('session_declined'),
  ];
  const maxCount = Math.max(...allCounts, 1);

  const bar = (count, teal) => {
    const w = Math.round((count / maxCount) * 100);
    const bg = teal ? color : '#6b7280';
    return (
      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:3 }}>
        <div style={{ flex:1, height:6, background:'#f3f4f6', borderRadius:4 }}>
          <div style={{ width:`${w}%`, height:'100%', background:bg, borderRadius:4, transition:'width 0.4s' }} />
        </div>
        <span style={{ fontSize:12, fontWeight:700, color:bg, minWidth:28, textAlign:'right' }}>{count}</span>
      </div>
    );
  };

  const stepCard = (label, count, accent) => (
    <div style={{ background:'#fff', border:`1px solid ${count > 0 ? '#e5e7eb' : '#f3f4f6'}`, borderRadius:8, padding:'8px 12px', marginBottom:6 }}>
      <div style={{ fontSize:12, fontWeight:600, color: count > 0 ? '#111827' : '#9ca3af' }}>{label}</div>
      {bar(count, accent !== false)}
    </div>
  );

  const colStyle = { flex:1, minWidth:0 };
  const colHeader = (title, bg) => (
    <div style={{ background: bg, color:'#fff', fontSize:11, fontWeight:700, textAlign:'center', padding:'4px 8px', borderRadius:6, marginBottom:6, letterSpacing:'0.04em' }}>
      {title}
    </div>
  );

  const sessionSteps = [
    { key:'session_warning', label:'Session Expiry Pop-up Shown' },
    { key:'slot_expired',    label:'Slot Reservation Expired' },
    { key:'session_extended',label:'Session extended' },
    { key:'session_declined',label:'Session extension declined' },
  ].filter(s => c(s.key) > 0);

  const sharedTail = (accent) => (
    <>
      {stepCard('Proceeded to Patient Form',      c('patient_form'),  accent)}
      {stepCard('Email and / or Phone validated', c('email_phone'),   accent)}
      {stepCard('Payment Initiated',              c('payment_init'),  accent)}
      {stepCard('Booking Completed',              c('booked'),        accent)}
    </>
  );

  return (
    <div>
      <SectionLabel>Booking Funnel</SectionLabel>

      {/* ── Shared top: flow started + route ── */}
      {stepCard('Booking flow started, appointment type selected', c('flow_started'))}

      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:'8px 12px', marginBottom:6 }}>
        <div style={{ fontSize:12, fontWeight:600, color: routeCount > 0 ? '#111827' : '#9ca3af' }}>Selected Booking Route</div>
        {bar(routeCount)}
        {(c('clinic_route_selected') > 0 || c('time_route_selected') > 0) && (
          <div style={{ display:'flex', gap:8, marginTop:4 }}>
            <span style={{ fontSize:11, color:color }}>By Clinic: {c('clinic_route_selected')}</span>
            <span style={{ fontSize:11, color:'#6b45c8' }}>By Time: {c('time_route_selected')}</span>
          </div>
        )}
      </div>

      {/* ── Two fully separate funnels ── */}
      <div style={{ display:'flex', gap:12, marginBottom:6 }}>

        {/* CLINIC PATH — full journey */}
        <div style={colStyle}>
          {colHeader('CLINIC PATH', color)}
          {stepCard('Selected Book by Clinic', c('clinic_route_selected'))}
          {stepCard('Shown Clinic Selection',  c('clinic_nav'))}
          {stepCard('Clinic Selected',         c('clinic_picked'))}
          {stepCard('Selected Time Block',     c('pref_time_selected'))}
          {stepCard('Shown Time Slots',        c('slots_browsed'))}
          {stepCard('Time Slot Selected',      c('slot_blocked'))}
          <div style={{ borderTop:'1px dashed #e5e7eb', margin:'6px 0' }} />
          {sharedTail(true)}
          {sessionSteps.length > 0 && (
            <>
              <div style={{ borderTop:'1px dashed #e5e7eb', margin:'6px 0' }} />
              {sessionSteps.map(s => <React.Fragment key={s.key}>{stepCard(s.label, c(s.key), true)}</React.Fragment>)}
            </>
          )}
        </div>

        {/* TIME PATH — full journey */}
        <div style={colStyle}>
          {colHeader('TIME PATH', '#6b45c8')}
          {stepCard('Selected Book by Time', c('time_route_selected'), false)}
          {stepCard('Shown Time Blocks',     c('pref_time_shown'),     false)}
          {stepCard('Selected Time Block',   c('pref_time_selected'),  false)}
          {stepCard('Clinics Selected',      c('clinic_picked'),       false)}
          {stepCard('Shown Time Slots',      c('slots_browsed'),       false)}
          {stepCard('Time Slot Selected',    c('slot_blocked'),        false)}
          <div style={{ borderTop:'1px dashed #e5e7eb', margin:'6px 0' }} />
          {sharedTail(false)}
          {sessionSteps.length > 0 && (
            <>
              <div style={{ borderTop:'1px dashed #e5e7eb', margin:'6px 0' }} />
              {sessionSteps.map(s => <React.Fragment key={s.key}>{stepCard(s.label, c(s.key), false)}</React.Fragment>)}
            </>
          )}
        </div>

      </div>

    </div>
  );
};

// ─── Drop-off Funnel (kept for cancelling/rescheduling FlowCards) ─────────────
const DropoffFunnel = ({ funnel, color }) => {
  if (!funnel?.length) return null;
  const max = Math.max(...funnel.map(r => r.count || 0), 1);
  return (
    <div>
      <SectionLabel>Drop-off stages</SectionLabel>
      {funnel.map((row, i) => {
        const w = Math.round(((row.count || 0) / max) * 100);
        return (
          <div key={i} style={{ marginBottom: 6 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'#374151', marginBottom:2 }}>
              <span>{row.label}</span><strong>{row.count}</strong>
            </div>
            <div style={{ background:'#f3f4f6', borderRadius:4, height:6 }}>
              <div style={{ width:`${w}%`, height:'100%', background:color, borderRadius:4 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Reschedule Funnel (styled like BookingFunnel) ────────────────────────────
const RescheduleFunnel = ({ funnel, color = '#6b45c8' }) => {
  if (!funnel?.length) return null;

  // Build count lookup by normalised label
  const byLabel = {};
  funnel.forEach(r => { byLabel[(r.label || '').toLowerCase().trim()] = r.count || 0; });

  const c = (...keys) => {
    for (const k of keys) {
      const v = byLabel[k.toLowerCase().trim()];
      if (v !== undefined) return v;
    }
    return 0;
  };

  const STEPS = [
    { label: 'Reschedule Flow Opened',       count: c('opened reschedule flow', 'reschedule flow started') },
    { label: 'Clinic Selected',              count: c('picked clinic', 'clinic selected') },
    { label: 'Time Block Selected',          count: c('selected time block', 'preference selected') },
    { label: 'Time Slot Selected',           count: c('time slot selected', 'slot reserved') },
    { label: 'Proceeded to Patient Form',    count: c('proceeded to patient form', 'navigate to appointment summary page') },
    { label: 'Payment Initiated',            count: c('payment initiated') },
    { label: 'Reschedule Completed',         count: c('booking reschedule completed', 'reschedule booking', 'booking completed') },
  ];

  const maxCount = Math.max(...STEPS.map(s => s.count), 1);

  const bar = (count) => {
    const w = Math.round((count / maxCount) * 100);
    return (
      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:3 }}>
        <div style={{ flex:1, height:6, background:'#f3f4f6', borderRadius:4 }}>
          <div style={{ width:`${w}%`, height:'100%', background: count > 0 ? color : '#e5e7eb', borderRadius:4, transition:'width 0.4s' }} />
        </div>
        <span style={{ fontSize:12, fontWeight:700, color: count > 0 ? color : '#9ca3af', minWidth:28, textAlign:'right' }}>{count}</span>
      </div>
    );
  };

  // Side events (non-zero only)
  const sideEvents = [
    { label: 'Unselected Preferred Time', count: c('unselected preferred time') },
    { label: 'Clinic Unselected',         count: c('clinic unselected') },
  ].filter(s => s.count > 0);

  return (
    <div>
      <SectionLabel>Reschedule Funnel</SectionLabel>
      {STEPS.map((step, i) => (
        <div key={i} style={{
          background:'#fff',
          border:`1px solid ${step.count > 0 ? '#e5e7eb' : '#f3f4f6'}`,
          borderRadius:8,
          padding:'8px 12px',
          marginBottom:6,
        }}>
          <div style={{ fontSize:12, fontWeight:600, color: step.count > 0 ? '#111827' : '#9ca3af' }}>
            {step.label}
          </div>
          {bar(step.count)}
        </div>
      ))}

      {sideEvents.length > 0 && (
        <>
          <div style={{ borderTop:'1px dashed #e5e7eb', margin:'8px 0 6px' }} />
          <div style={{ fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>Side Events</div>
          {sideEvents.map((s, i) => (
            <div key={i} style={{ background:'#fff', border:'1px solid #f3f4f6', borderRadius:8, padding:'8px 12px', marginBottom:4 }}>
              <div style={{ fontSize:12, fontWeight:600, color:'#374151' }}>{s.label}</div>
              {bar(s.count)}
            </div>
          ))}
        </>
      )}
    </div>
  );
};


// ─── Weekly Mini Chart (Area + Bar combo) ────────────────────────────────────
const WeeklyMiniChart = ({ weekly, valueKey = 'booked', totalKey = 'total', color, valueLabel = 'Booked' }) => {
  // Render even with a single data point — just show a flat line
  if (!weekly?.length) return null;
  const gradId = `wmc-${color.replace('#','')}`;
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total     = payload.find(p => p.dataKey === totalKey)?.value ?? 0;
    const completed = payload.find(p => p.dataKey === valueKey)?.value ?? 0;
    const cr = total ? ((completed / total) * 100).toFixed(1) : 0;
    return (
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 14px', fontSize:12, boxShadow:'0 4px 16px rgba(0,0,0,0.10)' }}>
        <div style={{ fontWeight:700, color:'#374151', marginBottom:6 }}>📅 {label}</div>
        <div style={{ color:'#6b7280' }}>Total: <strong style={{ color:'#111827' }}>{formatNumber(total)}</strong></div>
        <div style={{ color }}>{valueLabel}: <strong>{formatNumber(completed)}</strong></div>
        <div style={{ marginTop:4, color: cr >= 30 ? '#1a7f4b' : cr >= 15 ? '#b45309' : '#c0392b', fontWeight:700 }}>{cr}% CR</div>
      </div>
    );
  };
  return (
    <div>
      <div style={{ fontSize:11, fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Weekly Trend</div>
      <ResponsiveContainer width="100%" height={160}>
        <AreaChart data={weekly} margin={{ top:8, right:8, left:-24, bottom:0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.18} />
              <stop offset="95%" stopColor={color} stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis dataKey="week" tick={{ fontSize:10, fill:'#9ca3af' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize:10, fill:'#9ca3af' }} tickLine={false} axisLine={false} />
          <Tooltip content={<CustomTooltip />} />
          <Area dataKey={totalKey} type="monotone" stroke="#94a3b8" fill="#e2e8f0" strokeWidth={2} dot={false} name="Total" fillOpacity={0.7} />
          <Area dataKey={valueKey} type="monotone" stroke={color} fill={`url(#${gradId})`} strokeWidth={2.5} dot={{ r:3, fill:color, strokeWidth:2, stroke:'#fff' }} activeDot={{ r:5 }} name="Completed" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── Daily Mini Chart (7-day area chart — mirrors WeeklyMiniChart style) ──────
const DailyMiniChart = ({ daily, valueKey = 'booked', totalKey = 'total', color, valueLabel = 'Booked' }) => {
  if (!daily?.length) return null;
  const gradId = `dmc-${color.replace('#', '')}`;
  const data = daily.map(d => ({
    ...d,
    label: d.date ? new Date(d.date).toLocaleDateString('en-GB', { weekday:'short', day:'numeric' }) : d.date,
    cr: d[totalKey] ? +((d[valueKey] / d[totalKey]) * 100).toFixed(1) : 0,
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total     = payload.find(p => p.dataKey === totalKey)?.value ?? 0;
    const completed = payload.find(p => p.dataKey === valueKey)?.value ?? 0;
    const cr = total ? ((completed / total) * 100).toFixed(1) : 0;
    return (
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 14px', fontSize:12, boxShadow:'0 4px 16px rgba(0,0,0,0.10)' }}>
        <div style={{ fontWeight:700, color:'#374151', marginBottom:6 }}>📅 {label}</div>
        <div style={{ color:'#6b7280' }}>Total: <strong style={{ color:'#111827' }}>{formatNumber(total)}</strong></div>
        <div style={{ color }}>{valueLabel}: <strong>{formatNumber(completed)}</strong></div>
        <div style={{ marginTop:4, color: cr >= 30 ? '#1a7f4b' : cr >= 15 ? '#b45309' : '#c0392b', fontWeight:700 }}>{cr}% CR</div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ fontSize:11, fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Daily Trend</div>
      <ResponsiveContainer width="100%" height={160}>
        <AreaChart data={data} margin={{ top:8, right:8, left:-24, bottom:0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.18} />
              <stop offset="95%" stopColor={color} stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize:10, fill:'#9ca3af' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize:10, fill:'#9ca3af' }} tickLine={false} axisLine={false} />
          <Tooltip content={<CustomTooltip />} />
          <Area dataKey={totalKey} type="monotone" stroke="#94a3b8" fill="#e2e8f0" strokeWidth={2} dot={false} name="Total" fillOpacity={0.7} />
          <Area dataKey={valueKey} type="monotone" stroke={color} fill={`url(#${gradId})`} strokeWidth={2.5} dot={{ r:3, fill:color, strokeWidth:2, stroke:'#fff' }} activeDot={{ r:5 }} name={valueLabel} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};


// ─── Weekly Trend Table ───────────────────────────────────────────────────────
const th = { padding:'6px 10px', fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em', textAlign:'left', fontSize:11, whiteSpace:'nowrap', minWidth:60 };
const td = { padding:'7px 10px', fontSize:12, whiteSpace:'nowrap' };

/**
 * Convert an ISO week string like "2026-W15" to { start: "07 Apr", end: "13 Apr" }.
 * ISO weeks start on Monday and end on Sunday.
 */
const isoWeekDates = (weekStr) => {
  if (!weekStr) return { start: '—', end: '—' };
  const m = weekStr.match(/(\d{4})-W(\d{1,2})/);
  if (!m) return { start: '—', end: '—' };
  const year = +m[1], week = +m[2];
  // 4 Jan is always in week 1 of its year (ISO 8601)
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7; // Mon=1 … Sun=7
  const weekOneMonday = new Date(jan4);
  weekOneMonday.setDate(jan4.getDate() - dayOfWeek + 1);
  const monday = new Date(weekOneMonday);
  monday.setDate(weekOneMonday.getDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  return { start: fmt(monday), end: fmt(sunday) };
};

const WeeklyTable = ({ weekly, isBooking }) => {
  if (!weekly?.length) return null;
  const totals = weekly.reduce((acc, r) => {
    acc.total     += +(r.total)     || 0;
    acc.main      += isBooking ? (+(r.booked)    || 0) : (+(r.succeeded) || 0);
    acc.expired   += +(r.expired)   || 0;
    acc.abandoned += +(r.abandoned) || 0;
    acc.failed    += +(r.failed)    || 0;
    return acc;
  }, { total:0, main:0, expired:0, abandoned:0, failed:0 });
  const totalCr = totals.total ? +((totals.main / totals.total) * 100).toFixed(1) : 0;

  return (
    <div>
      <SectionLabel>Weekly Trend</SectionLabel>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ background:'#f9fafb' }}>
              <th style={th}>Week</th>
              <th style={{ ...th, textAlign:'right' }}>Sessions</th>
              <th style={{ ...th, textAlign:'right' }}>{isBooking ? 'Booked' : 'Completed'}</th>
              <th style={{ ...th, textAlign:'right' }}>CR%</th>
              {isBooking && <th style={{ ...th, textAlign:'right' }}>Expired</th>}
              <th style={{ ...th, textAlign:'right' }}>Abandoned</th>
              <th style={{ ...th, textAlign:'right' }}>Failed</th>
              {isBooking && <>
                <th style={{ ...th, textAlign:'right' }}>Clinic Bkd</th>
                <th style={{ ...th, textAlign:'right' }}>Clinic CR%</th>
                <th style={{ ...th, textAlign:'right' }}>Time Bkd</th>
                <th style={{ ...th, textAlign:'right' }}>Time CR%</th>
              </>}
            </tr>
          </thead>
          <tbody>
            {weekly.map((row, i) => {
              const color = crColor(row.cr);
              const { start, end } = isoWeekDates(row.week);
              return (
                <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                  <td style={{ ...td, whiteSpace:'nowrap', color:'#374151' }}>{start} – {end}</td>
                  <td style={{ ...td, textAlign:'right' }}>{formatNumber(row.total)}</td>
                  <td style={{ ...td, textAlign:'right', fontWeight:700, color:'#1a7f4b' }}>{formatNumber(isBooking ? row.booked : row.succeeded)}</td>
                  <td style={{ ...td, textAlign:'right', fontWeight:700, color }}>{row.cr}%</td>
                  {isBooking && <td style={{ ...td, textAlign:'right', color:'#c0392b' }}>{row.expired != null ? formatNumber(row.expired) : '—'}</td>}
                  <td style={{ ...td, textAlign:'right', color:'#b45309' }}>{row.abandoned != null ? formatNumber(row.abandoned) : '—'}</td>
                  <td style={{ ...td, textAlign:'right', color:'#c0392b' }}>{row.failed != null ? formatNumber(row.failed) : '—'}</td>
                  {isBooking && <>
                    <td style={{ ...td, textAlign:'right', color:'#008385' }}>{row.clinic_booked != null ? formatNumber(row.clinic_booked) : '—'}</td>
                    <td style={{ ...td, textAlign:'right', fontWeight:700, color:crColor(row.clinic_cr) }}>{row.clinic_cr ?? '—'}%</td>
                    <td style={{ ...td, textAlign:'right', color:'#6b45c8' }}>{row.time_booked != null ? formatNumber(row.time_booked) : '—'}</td>
                    <td style={{ ...td, textAlign:'right', fontWeight:700, color:crColor(row.time_cr) }}>{row.time_cr ?? '—'}%</td>
                  </>}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background:'#f9fafb', fontWeight:700, borderTop:'2px solid #e5e7eb' }}>
              <td style={{ ...td, color:'#374151', borderTop:'2px solid #e5e7eb' }}>Total</td>
              <td style={{ ...td, textAlign:'right', borderTop:'2px solid #e5e7eb' }}>{formatNumber(totals.total)}</td>
              <td style={{ ...td, textAlign:'right', color:'#1a7f4b', borderTop:'2px solid #e5e7eb' }}>{formatNumber(totals.main)}</td>
              <td style={{ ...td, textAlign:'right', color:crColor(totalCr), borderTop:'2px solid #e5e7eb' }}>{totalCr}%</td>
              {isBooking && <td style={{ ...td, textAlign:'right', color:'#c0392b', borderTop:'2px solid #e5e7eb' }}>{formatNumber(totals.expired)}</td>}
              <td style={{ ...td, textAlign:'right', color:'#b45309', borderTop:'2px solid #e5e7eb' }}>{formatNumber(totals.abandoned)}</td>
              <td style={{ ...td, textAlign:'right', color:'#c0392b', borderTop:'2px solid #e5e7eb' }}>{formatNumber(totals.failed)}</td>
              {isBooking && <><td colSpan={4} style={{ borderTop:'2px solid #e5e7eb' }} /></>}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ─── Daily Trend Table ────────────────────────────────────────────────────────
const DailyTable = ({ daily, isBooking }) => {
  if (!daily?.length) return null;
  const totals = daily.reduce((acc, r) => {
    acc.total     += +(r.total)     || 0;
    acc.main      += isBooking ? (+(r.booked) || 0) : (+(r.succeeded) || 0);
    acc.expired   += +(r.expired)   || 0;
    acc.abandoned += +(r.abandoned) || 0;
    acc.failed    += +(r.failed)    || 0;
    return acc;
  }, { total:0, main:0, expired:0, abandoned:0, failed:0 });
  const totalCr = totals.total ? +((totals.main / totals.total) * 100).toFixed(1) : 0;
  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit' }) +
      ' (' + dt.toLocaleDateString('en-GB', { weekday:'short' }) + ')';
  };
  return (
    <div>
      <SectionLabel>Daily Trend</SectionLabel>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
          <thead>
            <tr style={{ background:'#f9fafb' }}>
              <th style={th}>Date</th>
              <th style={{ ...th, textAlign:'right' }}>Sessions</th>
              <th style={{ ...th, textAlign:'right' }}>{isBooking ? 'Booked' : 'Completed'}</th>
              <th style={{ ...th, textAlign:'right' }}>CR%</th>
              {isBooking && <th style={{ ...th, textAlign:'right' }}>Expired</th>}
              <th style={{ ...th, textAlign:'right' }}>Abandoned</th>
              <th style={{ ...th, textAlign:'right' }}>Failed</th>
            </tr>
          </thead>
          <tbody>
            {daily.map((row, i) => {
              const main = isBooking ? (+(row.booked) || 0) : (+(row.succeeded) || 0);
              const cr = row.total ? +((main / row.total) * 100).toFixed(1) : 0;
              return (
                <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                  <td style={{ ...td, fontFamily:'monospace', color:'#6b7280' }}>{fmtDate(row.date)}</td>
                  <td style={{ ...td, textAlign:'right' }}>{formatNumber(row.total)}</td>
                  <td style={{ ...td, textAlign:'right', fontWeight:700, color:'#1a7f4b' }}>{formatNumber(main)}</td>
                  <td style={{ ...td, textAlign:'right', fontWeight:700, color:crColor(cr) }}>{cr}%</td>
                  {isBooking && <td style={{ ...td, textAlign:'right', color:'#c0392b' }}>{row.expired != null ? formatNumber(row.expired) : '—'}</td>}
                  <td style={{ ...td, textAlign:'right', color:'#b45309' }}>{row.abandoned != null ? formatNumber(row.abandoned) : '—'}</td>
                  <td style={{ ...td, textAlign:'right', color:'#c0392b' }}>{row.failed != null ? formatNumber(row.failed) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background:'#f9fafb', fontWeight:700, borderTop:'2px solid #e5e7eb' }}>
              <td style={{ ...td, color:'#374151', borderTop:'2px solid #e5e7eb' }}>Total</td>
              <td style={{ ...td, textAlign:'right', borderTop:'2px solid #e5e7eb' }}>{formatNumber(totals.total)}</td>
              <td style={{ ...td, textAlign:'right', color:'#1a7f4b', borderTop:'2px solid #e5e7eb' }}>{formatNumber(totals.main)}</td>
              <td style={{ ...td, textAlign:'right', color:crColor(totalCr), borderTop:'2px solid #e5e7eb' }}>{totalCr}%</td>
              {isBooking && <td style={{ ...td, textAlign:'right', color:'#c0392b', borderTop:'2px solid #e5e7eb' }}>{formatNumber(totals.expired)}</td>}
              <td style={{ ...td, textAlign:'right', color:'#b45309', borderTop:'2px solid #e5e7eb' }}>{formatNumber(totals.abandoned)}</td>
              <td style={{ ...td, textAlign:'right', color:'#c0392b', borderTop:'2px solid #e5e7eb' }}>{formatNumber(totals.failed)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ─── Appointment Update Funnel ────────────────────────────────────────────────
const AppointmentUpdateFunnel = ({ funnel, color = '#e07b39' }) => {
  if (!funnel?.length) return null;

  // Filter to steps that have a non-zero count and sort by count desc
  const steps = [...funnel]
    .filter(r => (r.count || 0) > 0)
    .sort((a, b) => b.count - a.count);

  if (!steps.length) return null;

  const max = steps[0].count || 1;

  const bar = (count) => {
    const w = Math.round((count / max) * 100);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
        <div style={{ flex: 1, height: 6, background: '#f3f4f6', borderRadius: 4 }}>
          <div style={{ width: `${w}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s' }} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 28, textAlign: 'right' }}>{count}</span>
      </div>
    );
  };

  return (
    <div>
      <SectionLabel color={color}>Appointment Update Funnel</SectionLabel>
      {steps.map((row, i) => (
        <div key={i} style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: '8px 12px',
          marginBottom: 6,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{row.label}</div>
          {bar(row.count)}
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{row.pct}% of sessions</div>
        </div>
      ))}
    </div>
  );
};

// ─── By Product Breakdown (Appointment Update) ────────────────────────────────
const ByProductBreakdown = ({ by_product, color = '#e07b39' }) => {
  if (!by_product?.length) return null;
  // hide if only the catch-all "General" row with 0 confirmed
  const meaningful = by_product.filter(r => r.total > 0);
  if (!meaningful.length) return null;
  return (
    <div>
      <SectionLabel color={color}>By Product</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {meaningful.map((row, i) => (
          <div key={i} style={{ padding: '10px 14px', border: `1px solid ${color}20`, borderRadius: 10, background: `${color}06` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{row.name}</span>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12 }}>
                <span style={{ color: '#6b7280' }}>Sessions: <strong style={{ color: '#111827' }}>{formatNumber(row.total)}</strong></span>
                <span style={{ color: '#1a7f4b', fontWeight: 700 }}>✓ {formatNumber(row.confirmed)}</span>
                <CRBadge cr={row.cr} />
              </div>
            </div>
            <ProgressBar pct={row.pct} color={color} />
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>{row.pct}% of total</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export {
  WeeklyMiniChart,
  DailyMiniChart,
  DropoffFunnel,
  RescheduleFunnel,
  BookingFunnel,
  RouteBreakdown,
  WeeklyTable,
  DailyTable,
  AppointmentTypeBreakdown,
  ProductCategoryBreakdown,
  DurationBreakdown,
  GenderBreakdown,
  LanguageBreakdown,
  ClinicPerformanceTable,
  TimeOfDayChart,
  DayOfWeekChart,
  AppointmentUpdateFunnel,
  ByProductBreakdown,
};

